import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, RefreshCw, TrendingUp, Layers, CreditCard, Coins } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CURRENCY_TONES = {
  USD: "bg-[#2BA8F0]",
  CAD: "bg-red-500",
  CLP: "bg-amber-500",
  DOP: "bg-blue-700",
  HTG: "bg-violet-600",
  EUR: "bg-indigo-500",
  BRL: "bg-emerald-600",
  MXN: "bg-teal-600",
};

export default function ExchangeRates() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("rates");
  const TABS = useMemo(() => ([
    { id: "rates", label: t("rates.title"), icon: TrendingUp },
    { id: "usd_pivot", label: t("rates.tab_usd_pivot", "USD Pivot Rates"), icon: Coins },
    { id: "tiers", label: t("rates.tab_tiers", "Convenience Fee Tiers"), icon: Layers },
    { id: "payku", label: t("rates.tab_payku", "Payku Payments"), icon: CreditCard },
  ]), [t]);

  return (
    <div className="space-y-6" data-testid="exchange-rates-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">{t("role.MASTER_ADMIN")}</div>
        <h1
          className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900"
          style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
        >
          {t("rates.title")}
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          {t("rates.subtitle")}
        </p>
      </div>

      <div className="flex border-b border-slate-200">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 -mb-px border-b-2 text-sm font-semibold ${
                tab === t.id
                  ? "border-[#0F62FE] text-[#0F62FE]"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "rates" && <RatesTab />}
      {tab === "usd_pivot" && <UsdPivotTab />}
      {tab === "tiers" && <TiersTab />}
      {tab === "payku" && <PaykuTab />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Payku Payments Tab — global settings (USD → CLP daily rate + platform fee)
// used ONLY for the "Pay in CLP via Payku" checkout. Independent from any
// per-user Local Currency configuration.
// ---------------------------------------------------------------------------
function PaykuTab() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [rate, setRate] = useState("");
  const [feePct, setFeePct] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/app-settings");
      setSettings(data);
      setRate(String(data.payku_usd_clp_rate ?? 950));
      setFeePct(String(data.payku_extra_fee_pct ?? 0));
    } catch (e) {
      toast.error(t("rates.payku_load_failed", "Failed to load Payku settings"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const r = Number(rate);
    const f = Number(feePct);
    if (!r || r <= 0) { toast.error(t("rates.payku_rate_invalid", "Enter a valid USD → CLP rate")); return; }
    if (isNaN(f) || f < 0 || f > 25) { toast.error(t("rates.payku_fee_invalid", "Fee must be between 0 and 25 percent")); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/app-settings", {
        payku_usd_clp_rate: r,
        payku_extra_fee_pct: f,
      });
      setSettings(data);
      toast.success(t("rates.payku_saved", "Payku settings saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("rates.payku_save_failed", "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center gap-2 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading")}
      </div>
    );
  }

  const usd = 100;
  const r = Number(rate) || 0;
  const f = Number(feePct) || 0;
  const previewClp = Math.round(usd * r * (1 + f / 100));
  const feeClp = Math.round(usd * r * (f / 100));

  return (
    <div className="max-w-2xl space-y-6" data-testid="payku-settings">
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="text-[10px] font-bold uppercase tracking-widest text-[#0F62FE] flex items-center gap-1.5 mb-1">
          <CreditCard className="w-3 h-3" /> {t("rates.payku_section_label", "Payku card payments")}
        </div>
        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {t("rates.payku_title", "USD → CLP conversion for Payku")}
        </h2>
        <p className="text-slate-500 text-sm mt-1 mb-5">
          {t("rates.payku_subtitle",
            "These values are separate from the Local Currency settings. They apply only to customers paying by card via Payku.")}
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              {t("rates.payku_rate_label", "Daily USD → CLP rate")}
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-mono">1 USD =</span>
              <input
                type="number"
                min="1"
                step="0.01"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                data-testid="payku-rate-input"
                className="flex h-10 w-full rounded-lg border border-slate-300 bg-white pl-20 pr-14 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs font-semibold">CLP</span>
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              {t("rates.payku_fee_label", "Extra Payku fee")}
            </label>
            <div className="relative">
              <input
                type="number"
                min="0"
                max="25"
                step="0.1"
                value={feePct}
                onChange={(e) => setFeePct(e.target.value)}
                data-testid="payku-fee-input"
                className="flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 pr-9 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-xs font-semibold">%</span>
            </div>
          </div>
        </div>

        {r > 0 && (
          <div className="mt-5 rounded-lg bg-[#0F62FE]/5 border border-[#0F62FE]/20 p-4 text-sm" data-testid="payku-preview">
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#0F62FE] mb-2">
              {t("rates.payku_preview_label", "Example — customer pays for $100 USD")}
            </div>
            <div className="grid grid-cols-3 gap-3 text-slate-800">
              <div>
                <div className="text-[10px] text-slate-500 uppercase tracking-widest">USD amount</div>
                <div className="font-bold text-base" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>${usd}</div>
              </div>
              <div>
                <div className="text-[10px] text-slate-500 uppercase tracking-widest">Base CLP</div>
                <div className="font-mono text-sm">CLP ${(usd * r).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-[10px] text-slate-500 uppercase tracking-widest">+ {f}% fee</div>
                <div className="font-mono text-sm">CLP ${feeClp.toLocaleString()}</div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-[#0F62FE]/20 flex items-center justify-between">
              <span className="text-slate-600 text-xs">Customer pays via Payku</span>
              <span className="font-bold text-lg text-[#0F62FE]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="payku-preview-total">
                CLP ${previewClp.toLocaleString()}
              </span>
            </div>
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            data-testid="payku-save-btn"
            className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Exchange Rates Tab
// ---------------------------------------------------------------------------
function RatesTab() {
  const { t } = useTranslation();
  const [rates, setRates] = useState([]);
  const [edits, setEdits] = useState({});  // {currency: stringValue}
  const [saving, setSaving] = useState({});  // {currency: bool}
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cash/exchange-rates");
      setRates(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("rates.failed_load_rates"));
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const save = async (ccy) => {
    const v = edits[ccy];
    const n = parseFloat(v);
    if (!n || n <= 0) { toast.error(t("rates.enter_valid_rate")); return; }
    setSaving((s) => ({ ...s, [ccy]: true }));
    try {
      await api.put("/cash/exchange-rates", { from_currency: ccy, to_currency: "HTG", rate: n });
      toast.success(t("rates.rate_updated", { ccy, n }));
      setEdits((e) => { const next = { ...e }; delete next[ccy]; return next; });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("rates.save_failed"));
    } finally { setSaving((s) => ({ ...s, [ccy]: false })); }
  };

  return (
    <div className="bg-white rounded-md border border-slate-200" data-testid="rates-tab">
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
        <div>
          <div className="text-sm font-semibold text-slate-900">{t("rates.exchange_rates_htg")}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {t("rates.rates_hint")}
          </div>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50"
          data-testid="reload-rates"
        >
          <RefreshCw className="w-3.5 h-3.5" /> {t("rates.reload")}
        </button>
      </div>
      {loading ? (
        <div className="px-5 py-12 text-center text-slate-500 text-sm">
          <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> {t("rates.loading_rates")}
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {rates.map((r) => {
            const ccy = r.from_currency;
            const isDirty = edits[ccy] !== undefined && parseFloat(edits[ccy]) !== Number(r.rate || 0);
            const tone = CURRENCY_TONES[ccy] || "bg-slate-500";
            return (
              <div key={ccy} className="flex items-center gap-3 px-5 py-3" data-testid={`rate-row-${ccy}`}>
                <div className={`${tone} w-12 h-12 rounded-md text-white flex items-center justify-center font-bold text-sm`}>
                  {ccy}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900">{t("rates.unit_htg", { ccy })}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {r.updated_by ? t("rates.last_updated_by", { name: r.updated_by }) : t("rates.no_rate_set")}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    step="0.0001"
                    min="0"
                    value={edits[ccy] !== undefined ? edits[ccy] : (r.rate ?? "")}
                    onChange={(e) => setEdits((s) => ({ ...s, [ccy]: e.target.value }))}
                    data-testid={`rate-input-${ccy}`}
                    placeholder="0.00"
                    className="h-9 w-32 rounded-md border border-slate-300 px-3 text-sm font-mono text-right focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
                  />
                  <span className="text-xs text-slate-500 font-semibold">HTG</span>
                  <button
                    onClick={() => save(ccy)}
                    disabled={!isDirty || saving[ccy]}
                    data-testid={`save-rate-${ccy}`}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#0F62FE] text-white text-xs font-semibold hover:bg-[#0050E6] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {saving[ccy] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    {t("rates.save")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fee Tiers Tab
// ---------------------------------------------------------------------------
function TiersTab() {
  const { t } = useTranslation();
  const [byCurrency, setByCurrency] = useState({});
  const [supported, setSupported] = useState([]);
  const [loading, setLoading] = useState(true);
  // Local working copy so the user can edit before clicking "Save changes"
  const [edits, setEdits] = useState({});  // {currency: [{min,max,max_fee}]}
  const [saving, setSaving] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cash/fee-tiers");
      const grouped = data.tiers_by_currency || {};
      setByCurrency(grouped);
      setEdits(JSON.parse(JSON.stringify(grouped)));   // deep copy for editing
      setSupported(data.supported_currencies || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("rates.failed_load_tiers"));
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const updateTier = (ccy, idx, field, value) => {
    setEdits((s) => {
      const next = { ...s };
      const tiers = [...(next[ccy] || [])];
      const tier = { ...tiers[idx] };
      if (field === "max_amount" && value === "") tier.max_amount = null;
      else tier[field] = value === "" ? "" : Number(value);
      tiers[idx] = tier;
      next[ccy] = tiers;
      return next;
    });
  };

  const addTier = (ccy) => {
    setEdits((s) => {
      const tiers = [...(s[ccy] || [])];
      const last = tiers[tiers.length - 1];
      const newMin = last ? (last.max_amount != null ? Number(last.max_amount) + 1 : 0) : 0;
      tiers.push({ min_amount: newMin, max_amount: null, max_fee: 0 });
      return { ...s, [ccy]: tiers };
    });
  };

  const removeTier = (ccy, idx) => {
    setEdits((s) => {
      const tiers = [...(s[ccy] || [])];
      tiers.splice(idx, 1);
      return { ...s, [ccy]: tiers };
    });
  };

  const isDirty = (ccy) => JSON.stringify(byCurrency[ccy] || []) !== JSON.stringify(edits[ccy] || []);

  const save = async (ccy) => {
    const tiers = (edits[ccy] || []).map((tier) => ({
      min_amount: Number(tier.min_amount) || 0,
      max_amount: tier.max_amount === "" || tier.max_amount == null ? null : Number(tier.max_amount),
      max_fee: Number(tier.max_fee) || 0,
    }));
    setSaving((s) => ({ ...s, [ccy]: true }));
    try {
      await api.put("/cash/fee-tiers", { currency: ccy, tiers });
      toast.success(t("rates.tiers_updated", { ccy, n: tiers.length }));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("rates.save_failed"));
    } finally { setSaving((s) => ({ ...s, [ccy]: false })); }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-md border border-slate-200 px-5 py-12 text-center text-slate-500 text-sm" data-testid="tiers-loading">
        <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> {t("rates.loading_tiers")}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="tiers-tab">
      <div className="rounded-md bg-blue-50 border border-blue-200 px-4 py-3 text-xs text-blue-900 leading-relaxed">
        {t("rates.tiers_hint_1")}
        <span className="font-bold"> {t("rates.tiers_hint_zero")} </span>{t("rates.tiers_hint_2")}
        <span className="font-bold"> {t("rates.tiers_hint_max_fee")} </span>{t("rates.tiers_hint_3")}
        <span className="font-semibold"> {t("rates.tiers_hint_max_amount")} </span>{t("rates.tiers_hint_4")}
      </div>
      {supported.map((ccy) => {
        const tiers = edits[ccy] || [];
        const tone = CURRENCY_TONES[ccy] || "bg-slate-500";
        return (
          <div key={ccy} className="bg-white rounded-md border border-slate-200 overflow-hidden" data-testid={`tier-section-${ccy}`}>
            <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-200">
              <div className={`${tone} w-10 h-10 rounded-md text-white flex items-center justify-center font-bold text-xs`}>
                {ccy}
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-900">{t("rates.currency_fee_tiers", { ccy })}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{tiers.length} {tiers.length === 1 ? t("rates.tier_singular") : t("rates.tier_plural")}</div>
              </div>
              <button
                onClick={() => addTier(ccy)}
                data-testid={`add-tier-${ccy}`}
                className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50"
              >
                <Plus className="w-3.5 h-3.5" /> {t("rates.add_tier")}
              </button>
              <button
                onClick={() => save(ccy)}
                disabled={!isDirty(ccy) || saving[ccy]}
                data-testid={`save-tiers-${ccy}`}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-[#0F62FE] text-white text-xs font-semibold hover:bg-[#0050E6] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {saving[ccy] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {t("rates.save_changes")}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    {[t("rates.col_min_amount", { ccy }), t("rates.col_max_amount", { ccy }), t("rates.col_max_fee", { ccy }), ""].map((h, i) => (
                      <th key={i} className="bg-slate-50 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 border-b border-slate-200">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tiers.length === 0 && (
                    <tr><td colSpan={4} className="px-5 py-6 text-center text-slate-500 text-sm">
                      {t("rates.no_tiers_for", { ccy })}
                    </td></tr>
                  )}
                  {tiers.map((tier, idx) => (
                    <tr key={idx} className="border-b border-slate-100">
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={tier.min_amount}
                          onChange={(e) => updateTier(ccy, idx, "min_amount", e.target.value)}
                          data-testid={`tier-${ccy}-${idx}-min`}
                          className="h-9 w-32 rounded-md border border-slate-300 px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={tier.max_amount == null ? "" : tier.max_amount}
                          onChange={(e) => updateTier(ccy, idx, "max_amount", e.target.value)}
                          data-testid={`tier-${ccy}-${idx}-max`}
                          placeholder="∞"
                          className="h-9 w-32 rounded-md border border-slate-300 px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={tier.max_fee}
                          onChange={(e) => updateTier(ccy, idx, "max_fee", e.target.value)}
                          data-testid={`tier-${ccy}-${idx}-fee`}
                          className="h-9 w-32 rounded-md border border-slate-300 px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0F62FE] bg-amber-50 font-semibold text-amber-800"
                        />
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => removeTier(ccy, idx)}
                          data-testid={`tier-${ccy}-${idx}-remove`}
                          className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-slate-200 text-rose-600 hover:bg-rose-50"
                          title={t("rates.remove_tier")}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// USD Pivot Rates Tab
// ---------------------------------------------------------------------------
// Each row expresses: `1 USD = usd_rate <currency>`.
// USD itself is fixed at 1.0 and read-only. Only MASTER_ADMIN can edit;
// everyone else sees the numbers but cannot change them.
// The "Preview conversion" panel calls /wallet/convert/preview so admins
// can sanity-check the pivot math before wiring the actual transfer flow.
function UsdPivotTab() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isMasterAdmin = user?.role === "MASTER_ADMIN";

  const [items, setItems] = useState([]);
  const [pivot, setPivot] = useState("USD");
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);

  // Preview state
  const [srcCcy, setSrcCcy] = useState("CLP");
  const [srcAmt, setSrcAmt] = useState("");
  const [dstCcy, setDstCcy] = useState("MXN");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/wallet/usd-rates");
      setItems(data.items || []);
      setPivot(data.pivot || "USD");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load USD pivot rates");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (ccy) => {
    const v = edits[ccy];
    const n = parseFloat(v);
    if (!n || n <= 0) { toast.error(t("rates.enter_valid_rate", "Enter a valid rate")); return; }
    setSaving((s) => ({ ...s, [ccy]: true }));
    try {
      await api.put("/wallet/usd-rates", { currency: ccy, usd_rate: n });
      toast.success(`1 USD = ${n} ${ccy} saved`);
      setEdits((e) => { const next = { ...e }; delete next[ccy]; return next; });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving((s) => ({ ...s, [ccy]: false })); }
  };

  const runPreview = async () => {
    const amt = parseFloat(srcAmt);
    if (!amt || amt <= 0) { toast.error("Enter a source amount > 0"); return; }
    if (srcCcy === dstCcy) { toast.error("Source and destination must differ"); return; }
    setPreviewLoading(true);
    try {
      const { data } = await api.post("/wallet/convert/preview", {
        source_currency: srcCcy,
        source_amount: amt,
        dest_currency: dstCcy,
      });
      setPreview(data);
    } catch (e) {
      setPreview(null);
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setPreviewLoading(false); }
  };

  const fmt = (n, digits = 6) => {
    if (n == null) return "—";
    const num = Number(n);
    if (!Number.isFinite(num)) return "—";
    // Trim trailing zeros for readability
    return num
      .toFixed(digits)
      .replace(/\.?0+$/, "")
      || "0";
  };

  return (
    <div className="space-y-4" data-testid="usd-pivot-tab">
      <div className="bg-white rounded-md border border-slate-200">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <div className="text-sm font-semibold text-slate-900">
              USD Pivot Rates · 1 {pivot} = X
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Every conversion between two Send Cash wallets is priced through USD.
              Formula: <span className="font-mono">dest = (source ÷ source_rate) × dest_rate</span>.
              {isMasterAdmin ? " Only you (Master Admin) can edit these numbers." : " Read-only for your role."}
            </div>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50"
            data-testid="reload-usd-rates"
          >
            <RefreshCw className="w-3.5 h-3.5" /> {t("rates.reload", "Reload")}
          </button>
        </div>
        {loading ? (
          <div className="px-5 py-12 text-center text-slate-500 text-sm">
            <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Loading…
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((r) => {
              const ccy = r.currency;
              const isUsd = ccy === "USD";
              const current = r.usd_rate;
              const isDirty =
                edits[ccy] !== undefined && parseFloat(edits[ccy]) !== Number(current || 0);
              const tone = CURRENCY_TONES[ccy] || "bg-slate-500";
              return (
                <div
                  key={ccy}
                  className="flex items-center gap-3 px-5 py-3"
                  data-testid={`usd-rate-row-${ccy}`}
                >
                  <div className={`${tone} w-12 h-12 rounded-md text-white flex items-center justify-center font-bold text-sm`}>
                    {ccy}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900">
                      1 USD = <span data-testid={`usd-rate-${ccy}`}>{current == null ? "—" : fmt(current, 6)}</span> {ccy}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {isUsd
                        ? "Pivot currency — fixed at 1.0"
                        : (r.updated_by
                            ? `Last updated by ${r.updated_by}`
                            : "Default value — never edited")}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.000001"
                      min="0"
                      value={isUsd ? "1" : (edits[ccy] !== undefined ? edits[ccy] : (current ?? ""))}
                      onChange={(e) => setEdits((s) => ({ ...s, [ccy]: e.target.value }))}
                      disabled={isUsd || !isMasterAdmin}
                      data-testid={`usd-rate-input-${ccy}`}
                      placeholder="0.00"
                      className="h-9 w-36 rounded-md border border-slate-300 px-3 text-sm font-mono text-right focus:outline-none focus:ring-2 focus:ring-[#0F62FE] disabled:bg-slate-50 disabled:text-slate-400"
                    />
                    <span className="text-xs text-slate-500 font-semibold">{ccy}</span>
                    <button
                      onClick={() => save(ccy)}
                      disabled={isUsd || !isMasterAdmin || !isDirty || saving[ccy]}
                      data-testid={`save-usd-rate-${ccy}`}
                      className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#0F62FE] text-white text-xs font-semibold hover:bg-[#0050E6] disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {saving[ccy] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      {t("rates.save", "Save")}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Conversion preview — pure math, no wallet is touched. */}
      <div
        className="bg-white rounded-md border border-slate-200 p-5"
        data-testid="usd-pivot-preview-card"
      >
        <div className="text-sm font-semibold text-slate-900">Preview conversion</div>
        <div className="text-xs text-slate-500 mt-0.5 mb-3">
          Dry-run — computes the pivot in USD and the destination amount using the rates above.
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              From
            </label>
            <select
              value={srcCcy}
              onChange={(e) => setSrcCcy(e.target.value)}
              data-testid="preview-src-ccy"
              className="h-9 w-full rounded-md border border-slate-300 px-2 text-sm bg-white"
            >
              {items.map((r) => (
                <option key={r.currency} value={r.currency}>{r.currency}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              Amount
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={srcAmt}
              onChange={(e) => setSrcAmt(e.target.value)}
              data-testid="preview-src-amount"
              placeholder="0.00"
              className="h-9 w-full rounded-md border border-slate-300 px-3 text-sm font-mono text-right"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
              To
            </label>
            <select
              value={dstCcy}
              onChange={(e) => setDstCcy(e.target.value)}
              data-testid="preview-dst-ccy"
              className="h-9 w-full rounded-md border border-slate-300 px-2 text-sm bg-white"
            >
              {items.map((r) => (
                <option key={r.currency} value={r.currency}>{r.currency}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={runPreview}
              disabled={previewLoading}
              data-testid="preview-run"
              className="inline-flex items-center justify-center gap-2 h-9 w-full rounded-md bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
            >
              {previewLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />}
              Compute
            </button>
          </div>
        </div>

        {preview && (
          <div
            className="mt-4 rounded-md border border-emerald-200 bg-emerald-50/60 p-4 grid grid-cols-2 sm:grid-cols-4 gap-3"
            data-testid="preview-result"
          >
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Source</div>
              <div className="text-sm font-bold text-slate-900 mt-0.5" data-testid="preview-source">
                {fmt(preview.source_amount, 2)} {preview.source_currency}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                rate: {fmt(preview.source_usd_rate, 6)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">USD pivot</div>
              <div className="text-sm font-bold text-[#0F62FE] mt-0.5" data-testid="preview-usd-pivot">
                ${fmt(preview.usd_pivot_amount, 4)}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">source ÷ source_rate</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Destination</div>
              <div className="text-sm font-bold text-emerald-700 mt-0.5" data-testid="preview-dest">
                {fmt(preview.dest_amount, 2)} {preview.dest_currency}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                rate: {fmt(preview.dest_usd_rate, 6)}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Formula</div>
              <div className="text-[11px] text-slate-700 mt-0.5 font-mono leading-relaxed">
                ({fmt(preview.source_amount, 2)} ÷ {fmt(preview.source_usd_rate, 4)})
                × {fmt(preview.dest_usd_rate, 4)}
                = {fmt(preview.dest_amount, 2)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

